import { forwardRef } from 'react';
import { autoUpdate, FloatingPortal, flip, offset, shift, useFloating } from '@floating-ui/react';

import { Button, ButtonProps, FlexBox, IconComponent, Text } from '@blockworks/ui';
import { useMergeRefs } from '@blockworks/ui/hooks';
import { cn } from '@blockworks/ui/system';

import { useVisualizationOptionsContext } from '../../context';

const sizeStyles = {
    xxs: 'h-[22px] w-[22px] px-0 flex items-center justify-center',
    xs: 'h-[24px] w-[24px] px-0 flex items-center justify-center',
};

interface FeatureControlButtonProps extends Omit<ButtonProps, 'children' | 'leadingIcon' | 'trailingIcon' | 'intent'> {
    intent: Extract<ButtonProps['intent'], 'primary-soft' | 'neutral'>;
    icon: IconComponent;
    className?: string;
    floatingLabel?: string;
}

/** New dumb button instantiation that can inherit top level sizing props from the layout context */
const FeatureControlButton = forwardRef<HTMLButtonElement, FeatureControlButtonProps>(function FeatureButton(
    { icon: Icon, className, floatingLabel, ...rest },
    ref,
) {
    const {
        layout: { controlSize },
    } = useVisualizationOptionsContext();

    const { refs, floatingStyles } = useFloating({
        placement: 'top',
        middleware: [offset(4), flip(), shift()],
        whileElementsMounted: autoUpdate,
        strategy: 'fixed',
    });

    const mergeRefs = useMergeRefs(ref, refs.setReference);

    return (
        <>
            {!!floatingLabel && (
                <FloatingPortal>
                    <FlexBox
                        ref={refs.setFloating}
                        border={1}
                        borderRadius="md"
                        bgColor="base"
                        style={floatingStyles}
                        py={1.5}
                        px={2}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Text size="xxs" color="muted">
                            {floatingLabel}
                        </Text>
                    </FlexBox>
                </FloatingPortal>
            )}
            <Button
                ref={mergeRefs}
                {...rest}
                borderRadius={'md'}
                className={cn(sizeStyles[controlSize], className)}
                size={controlSize}
            >
                <Icon className={controlSize === 'xxs' ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
            </Button>
        </>
    );
});

export type { FeatureControlButtonProps };
export { FeatureControlButton };
